import { ComponentType, useRef } from "react";
import { Box } from "@mui/material";
import { Button } from "@vilocnv/allsetra-core";
import { CSVLink as ReactCsvLink } from "react-csv";
import { useTranslation } from "react-i18next";
import { Export } from "assets/icons";

// Data
import {
  CsvColumn,
  CsvHeader,
  CsvRow,
  buildCsvFilename,
  buildCsvHeaders,
  buildCsvRows,
  escapeCsvRows
} from "app/data/helpers";

// react-csv's class component types omit `refs`, which breaks CRA's JSX check
// (TS2786) on the Oryx/Node 22 deploy image. Cast keeps runtime behavior.
type CsvLinkProps = {
  data: CsvRow[];
  headers: CsvHeader[];
  filename: string;
  separator?: string;
  uFEFF?: boolean;
};

const CSVLink = ReactCsvLink as unknown as ComponentType<CsvLinkProps>;

export interface ExportCsvButtonProps<T> {
  /** The rows currently rendered by the table — exported as they are. */
  data: T[] | null | undefined;
  columns: CsvColumn<T>[];
  /** Filename stem; the export date and `.csv` are appended. */
  filenamePrefix: string;
  disabled?: boolean;
  id: string;
}

/**
 * Downloads the rows it is handed as a CSV file.
 *
 * Serialization is `react-csv`'s — the same dependency the Reports export
 * already uses. It quotes every field, which covers separators and line
 * breaks; quotes *inside* a value it does not escape, so the rows go through
 * `escapeCsvRows` first. The BOM makes Excel read the file as UTF-8 instead of
 * the local ANSI codepage.
 *
 * The visible control is the core `Button` — the same component, with the same
 * `variant`, that `ReactTable` renders `primaryButton` and `secondaryButton`
 * with, so the export sits in the header at the size and colour of the buttons
 * beside it rather than as a bare MUI button.
 *
 * The `CSVLink` anchor is hidden next to it and clicked through the DOM.
 * Rendering the button *as* the CSVLink (`component={CSVLink}`) looks tidier
 * but crashes: MUI forwards a ref into what is a class component, and its
 * focus-visible handler then reads `ownerDocument` off the instance. Keeping
 * them separate also means the disabled state is a genuinely inert `<button>`
 * rather than an anchor that still navigates.
 */
const ExportCsvButton = <T,>({
  data,
  columns,
  filenamePrefix,
  disabled = false,
  id
}: ExportCsvButtonProps<T>) => {
  const { t } = useTranslation(["translation"]);
  const linkContainer = useRef<HTMLDivElement | null>(null);

  const rows = buildCsvRows(data, columns);
  const isDisabled = disabled || rows.length === 0;

  const label = t("buttonsText.exportCsv");

  const onExport = () => {
    linkContainer.current?.querySelector("a")?.click();
  };

  return (
    <>
      <Button
        id={id}
        variant="outlined"
        disabled={isDisabled}
        startIcon={<Export />}
        onClick={onExport}
        sx={{
          whiteSpace: "nowrap",
          // The Export SVG hardcodes a purple fill. Inheriting the button's
          // colour keeps the icon on the palette the sibling buttons use, and
          // greys out with them when the action is disabled.
          "& svg path": { fill: "currentColor" }
        }}
      >
        {label}
      </Button>
      {!isDisabled && (
        <Box ref={linkContainer} sx={{ display: "none" }} aria-hidden>
          <CSVLink
            data={escapeCsvRows(rows)}
            headers={buildCsvHeaders(columns)}
            filename={buildCsvFilename(filenamePrefix)}
            separator=","
            uFEFF
          />
        </Box>
      )}
    </>
  );
};

export default ExportCsvButton;
